import { Botao } from './Botao'
import { IconeSeta } from './Icones'

interface Props {
  pagina: number
  totalPaginas: number
  totalElementos: number
  aoMudar: (pagina: number) => void
  /**
   * O que está sendo contado, no singular e no plural.
   *
   * Tem padrão porque a barra nasceu na lista de solicitações e continua sendo usada lá sem
   * mudança nenhuma. Passa a ser dito quando a lista é de outra coisa: "12 solicitações no total"
   * sob uma tabela de ordens de serviço é a barra afirmando o nome errado do que a pessoa está
   * lendo — e o público desta tela inclui quem não sabe que os dois módulos existem.
   */
  nomeDoItem?: { singular: string; plural: string }
}

/**
 * Navegação entre páginas.
 *
 * Mostra "Página X de Y — N solicitações no total", e não só as setas: o fiscal precisa
 * saber quantas solicitações atendem ao filtro para citar o número no relatório mensal —
 * no sistema anterior esse total era contado à mão.
 *
 * O total fica em região `aria-live`: quem não vê a tela precisa ouvir que o filtro passou
 * de 1027 para 12 registros, senão só descobre navegando a lista inteira.
 *
 * @param props página atual (base 1), totais e o que fazer ao mudar
 * @returns a barra de paginação
 */
export function Paginacao({
  pagina,
  totalPaginas,
  totalElementos,
  aoMudar,
  nomeDoItem = { singular: 'solicitação', plural: 'solicitações' },
}: Props) {
  // O total aparece SEMPRE — é exatamente com uma página (o filtro apurado do fechamento)
  // que o fiscal mais precisa do número para citar no termo. Só as setas dependem de haver
  // mais de uma página.
  return (
    <nav
      aria-label="Paginação da lista"
      className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4"
    >
      <p className="text-sm text-slate-600" aria-live="polite">
        {totalPaginas > 1 ? `Página ${pagina} de ${totalPaginas} — ` : ''}
        {totalElementos} {totalElementos === 1 ? nomeDoItem.singular : nomeDoItem.plural} no total
      </p>
      {totalPaginas > 1 && (
        <div className="flex gap-2">
          <Botao tamanho="pequeno" onClick={() => aoMudar(pagina - 1)} disabled={pagina <= 1}>
            <IconeSeta className="h-4 w-4 rotate-180" />
            Anterior
          </Botao>
          <Botao tamanho="pequeno" onClick={() => aoMudar(pagina + 1)} disabled={pagina >= totalPaginas}>
            Próxima
            <IconeSeta className="h-4 w-4" />
          </Botao>
        </div>
      )}
    </nav>
  )
}
